import { shorten } from '@polaris/core/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { TextSearchIcon } from 'lucide-react'
import type { ComponentProps } from 'react'
import { cn } from '@/src/utils/styles'

const nsrVariants = cva('flex flex-col items-center justify-center', {
  variants: {
    size: {
      default: 'px-3 py-8 gap-7',
      small: 'py-4 px-3 gap-3',
    },
  },
  defaultVariants: {
    size: 'default',
  },
})

interface NoSearchResultsProps
  extends ComponentProps<'div'>,
    VariantProps<typeof nsrVariants> {
  failedQuery?: string
  hideAdjustQueryMessage?: boolean
}

const NoSearchResults = ({
  className,
  size = 'default',
  failedQuery,
  hideAdjustQueryMessage,
  ref,
  ...props
}: NoSearchResultsProps) => {
  let splashMsg = 'No results'
  if (failedQuery) {
    splashMsg = `No results for "${shorten(failedQuery, {
      prefixLength: 10,
      suffixLength: 10,
    })}"`
  }

  return (
    <div ref={ref} className={cn(nsrVariants({ size, className }))} {...props}>
      <div
        className={cn('flex items-center justify-center opacity-20', {
          'w-12 h-12': size === 'default',
        })}
      >
        <TextSearchIcon size={32} className='text-primary' />
      </div>
      <div className='flex flex-col gap-2 text-center'>
        <h6
          className={cn('text-primary font-semibold', {
            'text-xl': size === 'default',
            'text-sm': size === 'small',
          })}
        >
          {splashMsg}
        </h6>
        <p
          className={cn('text-secondary', {
            'text-base': size === 'default',
            'text-xs': size === 'small',
            hidden: hideAdjustQueryMessage,
          })}
        >
          Try adjusting your search query
        </p>
      </div>
    </div>
  )
}

NoSearchResults.displayName = 'NoSearchResults'

export { NoSearchResults }
